import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { MutateKeys } from "../constants/MutateKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TileListDownloadParams,
  FetchInvoiceFiltersDataType,
  FetchInvoiceFiltersType,
  FetchInvoiceListDataType,
  FetchInvoiceListParamsType,
  TileListParamsType,
  UpdateInvoiceStatusType,
  ListViewSocketActionPayload,
  BulkStatusUpdatePayload,
  InvoiceProgressType,
} from "../Types/invoiceManagementTypes";
import { cleanParams, encodeArrayObjects } from "../Utilities/queryParamUtils";
import { generatePath } from "react-router-dom";
import {
  GenerationStatus,
  InvoiceFilterIds,
} from "../enums/InvoiceManagementEnums";

const InvoiceManagementServices = () => {
  const queryClient = useQueryClient();
  const {
    FETCH_INVOICE_MANAGEMENT_TILE,
    INITIATE_INVOICE_DOWNLOAD,
    INITIATE_INVOICE_CARRIER_DOWNLOAD,
    INITIATE_EXPORT_TO_QUICKBOOKS,
    FETCH_INVOICE_MANAGEMENT_LIST,
    FETCH_INVOICE_MANAGEMENT_FILTERS,
    UPDATE_INVOICE_STATUS,
    ARCHIVE_INVOICE_ROUTE,
    GET_PRESIGNED_URL_ROUTE,
    BULK_SYNC_STATUS_UPDATE,
    BULK_STATUS_UPDATE,
    INVOICE_RECALL_ACTION,
    INITIATE_DOWNLOAD_ORIGINAL_FILE,
    FETCH_INVOICE_GENERATION_STATUS,
    GET_QUICKBOOKS_SYNC_STATUS
  } = ApiRoutes;

  const {
    INVOICE_MANAGEMENT_TILE_KEY,
    FETCH_INVOICE_MANAGEMENT_CUSTOMER_FILTER_KEY,
    FETCH_INVOICE_MANAGEMENT_JOBS_FILTER_KEY,
    FETCH_INVOICE_MANAGEMENT_ORDERS_FILTER_KEY,
    FETCH_INVOICE_MANAGEMENT_SYNC_STATUS_FILTER_KEY,
    FETCH_INVOICE_MANAGEMENT_INVOICES_FILTER_KEY,
    FETCH_INVOICE_MANAGEMENT_INVOICE_STATUS_FILTER_KEY,
    FETCH_INVOICE_MANAGEMENT_LIST_KEY,
    FETCH_INVOICE_GENERATION_STATUS_KEY,
  } = QueryKeys;

  const {
    UPDATE_INVOICE_STATUS_KEY,
    ARCHIVE_INVOICE_KEY,
    GET_TILE_INVOICE_DOWNLOAD_KEY,
    GET_LIST_INVOICE_DOWNLOAD_KEY,
    GET_LIST_INVOICE_CARRIER_DOWNLOAD_KEY,
    GET_QUICKBOOK_SYNC_STATUS_KEY,
    GET_LIST_QUICKBOOKS_EXPORT_KEY,
    GET_PRESIGNED_URL_KEY,
    BULK_SYNC_STATUS_UPDATE_KEY,
    INITIATE_RECALL_ACTION_KEY,
    GET_RECALL_INVOICE_DETAILS_KEY,
    INITIATE_DOWNLOAD_ORIGINAL_FILE_KEY,
    BULK_STATUS_UPDATE_KEY,
  } = MutateKeys;

  const GetTileDownload = () => {
    const getTileDownload = async (params: TileListDownloadParams) => {
      const cleanedParams = cleanParams(params);
      const { data } = await axiosInstance.get(INITIATE_INVOICE_DOWNLOAD, {
        params: cleanedParams,
      });
      return data;
    };

    return useMutation({
      mutationFn: getTileDownload,
      mutationKey: [GET_TILE_INVOICE_DOWNLOAD_KEY],
    });
  };

  const GetListDownload = () => {
    const getListDownload = async (
      payloadData: ListViewSocketActionPayload,
    ) => {
      const payload = {
        ...payloadData,
      };
      const { data } = await axiosInstance.post(
        INITIATE_INVOICE_DOWNLOAD,
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: getListDownload,
      mutationKey: [GET_LIST_INVOICE_DOWNLOAD_KEY],
    });
  };

  // getListCarrierDownload is named for truck Invoice Options
  const GetListCarrierDownload = () => {
    const getListCarrierDownload = async (
      payloadData: ListViewSocketActionPayload,
    ) => {
      const payload = {
        ...payloadData,
      };
      const { data } = await axiosInstance.post(
        INITIATE_INVOICE_CARRIER_DOWNLOAD,
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: getListCarrierDownload,
      mutationKey: [GET_LIST_INVOICE_CARRIER_DOWNLOAD_KEY],
    });
  };

  const GetQuickBookSyncStatus = (payload) => {
    const getQuickBookSyncStatus = async (
      payloadData: ListViewSocketActionPayload,
    ) => {
      const payload = {
        ...payloadData,
      };
      const { data } = await axiosInstance.post(
        GET_QUICKBOOKS_SYNC_STATUS,
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: getQuickBookSyncStatus,
      mutationKey: [GET_QUICKBOOK_SYNC_STATUS_KEY, payload],
    });
  };

  const ExportSelectedList = () => {
    const exportSelectedListItems = async (
      payloadData: ListViewSocketActionPayload,
    ) => {
      const payload = {
        ...payloadData,
      };
      const { data } = await axiosInstance.post(
        INITIATE_EXPORT_TO_QUICKBOOKS,
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: exportSelectedListItems,
      mutationKey: [GET_LIST_QUICKBOOKS_EXPORT_KEY],
    });
  };

  const FetchTileViewList = (
    params: TileListParamsType,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const cleanedParams = cleanParams({
      ...params,
    });
    const fetchTileViewList = async () => {
      const { data } = await axiosInstance.get(FETCH_INVOICE_MANAGEMENT_TILE, {
        params: {
          ...cleanedParams,
          setDefaultPageSize: pageSizeChanged,
        },
      });
      return data;
    };

    return useQuery({
      queryFn: fetchTileViewList,
      queryKey: [INVOICE_MANAGEMENT_TILE_KEY, cleanedParams],
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag,
    });
  };

  const FetchListViewData = (
    params: FetchInvoiceListParamsType,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const payload = { ...params, setDefaultPageSize: pageSizeChanged };
    const fetchListViewData = async (): Promise<FetchInvoiceListDataType> => {
      const { data } = await axiosInstance.post<FetchInvoiceListDataType>(
        FETCH_INVOICE_MANAGEMENT_LIST,
        payload,
      );
      return data;
    };

    return useQuery({
      queryFn: fetchListViewData,
      queryKey: [FETCH_INVOICE_MANAGEMENT_LIST_KEY, params],
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag,
    });
  };

  const FetchCustomerFilter = (params: FetchInvoiceFiltersType) => {
    const fetchCustomerFilter =
      async (): Promise<FetchInvoiceFiltersDataType> => {
        const url = generatePath(FETCH_INVOICE_MANAGEMENT_FILTERS, {
          filterId: InvoiceFilterIds.CUSTOMERS,
        });
        const { data } = await axiosInstance.post<FetchInvoiceFiltersDataType>(
          url,
          params,
        );
        return data;
      };
    return useQuery({
      queryFn: fetchCustomerFilter,
      queryKey: [FETCH_INVOICE_MANAGEMENT_CUSTOMER_FILTER_KEY],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchJobsFilter = (params: FetchInvoiceFiltersType) => {
    const fetchJobsFilter = async (): Promise<FetchInvoiceFiltersDataType> => {
      const url = generatePath(FETCH_INVOICE_MANAGEMENT_FILTERS, {
        filterId: InvoiceFilterIds.JOBS,
      });
      const { data } = await axiosInstance.post<FetchInvoiceFiltersDataType>(
        url,
        params,
      );
      return data;
    };

    return useQuery({
      queryFn: fetchJobsFilter,
      queryKey: [FETCH_INVOICE_MANAGEMENT_JOBS_FILTER_KEY],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchOrdersFilter = (params: FetchInvoiceFiltersType) => {
    const fetchOrdersFilter =
      async (): Promise<FetchInvoiceFiltersDataType> => {
        const url = generatePath(FETCH_INVOICE_MANAGEMENT_FILTERS, {
          filterId: InvoiceFilterIds.ORDERS,
        });
        const { data } = await axiosInstance.post<FetchInvoiceFiltersDataType>(
          url,
          params,
        );
        return data;
      };

    return useQuery({
      queryFn: fetchOrdersFilter,
      queryKey: [FETCH_INVOICE_MANAGEMENT_ORDERS_FILTER_KEY],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchInvoicesFilter = (params: FetchInvoiceFiltersType) => {
    const fetchInvoicesFilter =
      async (): Promise<FetchInvoiceFiltersDataType> => {
        const url = generatePath(FETCH_INVOICE_MANAGEMENT_FILTERS, {
          filterId: InvoiceFilterIds.INVOICES,
        });
        const { data } = await axiosInstance.post<FetchInvoiceFiltersDataType>(
          url,
          params,
        );
        return data;
      };

    return useQuery({
      queryFn: fetchInvoicesFilter,
      queryKey: [FETCH_INVOICE_MANAGEMENT_INVOICES_FILTER_KEY],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchSyncStatusFilter = (params: FetchInvoiceFiltersType) => {
    const fetchSyncStatusFilter =
      async (): Promise<FetchInvoiceFiltersDataType> => {
        const url = generatePath(FETCH_INVOICE_MANAGEMENT_FILTERS, {
          filterId: InvoiceFilterIds.SYNC_STATUS,
        });
        const { data } = await axiosInstance.post<FetchInvoiceFiltersDataType>(
          url,
          params,
        );
        return data;
      };

    return useQuery({
      queryFn: fetchSyncStatusFilter,
      queryKey: [FETCH_INVOICE_MANAGEMENT_SYNC_STATUS_FILTER_KEY],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchInvoiceStatusFilter = (params: FetchInvoiceFiltersType) => {
    const fetchInvoiceStatusFilter =
      async (): Promise<FetchInvoiceFiltersDataType> => {
        const url = generatePath(FETCH_INVOICE_MANAGEMENT_FILTERS, {
          filterId: InvoiceFilterIds.INVOICE_STATUS,
        });
        const { data } = await axiosInstance.post<FetchInvoiceFiltersDataType>(
          url,
          params,
        );
        return data;
      };

    return useQuery({
      queryFn: fetchInvoiceStatusFilter,
      queryKey: [FETCH_INVOICE_MANAGEMENT_INVOICE_STATUS_FILTER_KEY],
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const UpdateInvoiceStatus = () => {
    const updateInvoiceStatus = async (params: UpdateInvoiceStatusType) => {
      const { invoiceId, ...rest } = params;
      const url = generatePath(UPDATE_INVOICE_STATUS, {
        invoiceId,
      });
      const payload = {
        [rest.key]: rest.status,
      };
      const { data } = await axiosInstance.patch(url, { ...payload });
      return data;
    };
    return useMutation({
      mutationFn: updateInvoiceStatus,
      mutationKey: [UPDATE_INVOICE_STATUS_KEY],
      onSuccess: (updatedInvoice) => {
        queryClient.setQueriesData(
          { queryKey: [FETCH_INVOICE_MANAGEMENT_LIST_KEY] },
          (oldData) => {
            if (!oldData) return;
            const updatedList = oldData.data?.map((invoice) =>
              invoice?.id === updatedInvoice?.id ? updatedInvoice : invoice,
            );

            return {
              ...oldData,
              data: updatedList,
            };
          },
        );
      },
    });
  };

  const ArchiveInvoiceAction = () => {
    const archiveInvoiceAction = async (invoiceId?: string) => {
      const url = generatePath(ARCHIVE_INVOICE_ROUTE, {
        invoiceId: invoiceId,
      });
      const { data } = await axiosInstance.post(url);
      return data;
    };
    return useMutation({
      mutationFn: archiveInvoiceAction,
      mutationKey: [ARCHIVE_INVOICE_KEY],
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [FETCH_INVOICE_MANAGEMENT_LIST_KEY],
        }),
    });
  };

  const GetPreSignedInvoiceUrl = () => {
    const getPresignedInvoiceUrl = async (invoiceId?: string) => {
      const url = generatePath(GET_PRESIGNED_URL_ROUTE, {
        invoiceId: invoiceId,
      });
      const { data } = await axiosInstance.get(url);
      return data;
    };
    return useMutation({
      mutationFn: getPresignedInvoiceUrl,
      mutationKey: [GET_PRESIGNED_URL_KEY],
    });
  };

  const BulkSyncStatusUpdate = () => {
    const bulkSyncStatusUpdate = async (
      payload?: ListViewSocketActionPayload,
    ) => {
      const cleanedParams = cleanParams(payload);
      const { data } = await axiosInstance.post(BULK_SYNC_STATUS_UPDATE, {
        ...cleanedParams,
      });
      return data;
    };
    return useMutation({
      mutationFn: bulkSyncStatusUpdate,
      mutationKey: [BULK_SYNC_STATUS_UPDATE_KEY],
    });
  };

  const BulkStatusUpdate = () => {
    const bulkStatusUpdate = async (payload?: BulkStatusUpdatePayload) => {
      const cleanedParams = cleanParams(payload);
      const { data } = await axiosInstance.patch(BULK_STATUS_UPDATE, {
        ...cleanedParams,
      });
      return data;
    };
    return useMutation({
      mutationFn: bulkStatusUpdate,
      mutationKey: [BULK_STATUS_UPDATE_KEY],
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [FETCH_INVOICE_MANAGEMENT_LIST_KEY],
        });
      },
    });
  };

  const RecallSelectedInvoiceBatch = () => {
    const recallSelectedInvoiceBatch = async (invoiceId: string) => {
      const url = generatePath(INVOICE_RECALL_ACTION, { invoiceId: invoiceId });
      const { data } = await axiosInstance.post(url);
      return data;
    };

    return useMutation({
      mutationFn: recallSelectedInvoiceBatch,
      mutationKey: [INITIATE_RECALL_ACTION_KEY],
    });
  };

  const GetRecallInvoiceDetails = () => {
    const getRecallInvoiceDetailsKey = async (invoiceId: string) => {
      const url = generatePath(INVOICE_RECALL_ACTION, { invoiceId: invoiceId });
      const { data } = await axiosInstance.get(url);
      return data;
    };

    return useMutation({
      mutationFn: getRecallInvoiceDetailsKey,
      mutationKey: [GET_RECALL_INVOICE_DETAILS_KEY],
    });
  };

  const DownloadOriginalInvoiceTickets = () => {
    const downloadOriginalInvoiceTickets = async (invoiceId: string) => {
      const url = generatePath(INITIATE_DOWNLOAD_ORIGINAL_FILE, {
        invoiceId: invoiceId,
      });
      const { data } = await axiosInstance.post(url);
      return data;
    };

    return useMutation({
      mutationFn: downloadOriginalInvoiceTickets,
      mutationKey: [INITIATE_DOWNLOAD_ORIGINAL_FILE_KEY],
    });
  };

  const FetchInvoiceGenerationStatus = () => {
    const fetchInvoiceGenerationStatus =
      async (): Promise<InvoiceProgressType> => {
        const { data } = await axiosInstance.get<InvoiceProgressType>(
          FETCH_INVOICE_GENERATION_STATUS,
        );
        return data;
      };

    return useQuery({
      queryKey: [FETCH_INVOICE_GENERATION_STATUS_KEY],
      queryFn: fetchInvoiceGenerationStatus,
      refetchOnMount: true,
      refetchInterval: (data) => {
        if (data?.state?.data?.status === GenerationStatus.IN_PROGRESS) {
          return 90 * 1000; // refresh every 90 seconds
        }

        return false;
      },
    });
  };

  return {
    GetTileDownload,
    GetListDownload,
    GetListCarrierDownload,
    GetQuickBookSyncStatus,
    ExportSelectedList,
    FetchTileViewList,
    FetchListViewData,
    UpdateInvoiceStatus,
    ArchiveInvoiceAction,
    GetPreSignedInvoiceUrl,
    BulkSyncStatusUpdate,
    FetchCustomerFilter,
    FetchJobsFilter,
    FetchOrdersFilter,
    FetchInvoicesFilter,
    FetchSyncStatusFilter,
    FetchInvoiceStatusFilter,
    BulkStatusUpdate,
    RecallSelectedInvoiceBatch,
    GetRecallInvoiceDetails,
    DownloadOriginalInvoiceTickets,
    FetchInvoiceGenerationStatus,
  };
};

export default InvoiceManagementServices;
